import { Injectable, inject } from '@angular/core';
import { combineLatest, map, Observable } from 'rxjs';
import { Equipment } from '../../../../models/equipment.model';
import { Repair } from '../../../../models/repair.model';
import { BulkWorkOrderSharedDataService } from '../../../../services/bulk-work-order-shared-data.service';
import { BulkWorkOrderManufacturerError } from '../../../../models/bulk-work-order/bulk-work-order-manufacturer-error.model';

const REEFER_MODE_CODE = '43';

/**
 * ContainerManufacturerValidator
 *
 * Validates selected repair codes against reefer unit manufacturer constraints.
 * Only applies for mode '43' (reefer repair).
 *
 * Rules:
 * 1. If the bulk work order contains reefer containers from DIFFERENT manufacturers,
 *    only repair codes starting with '0' are permitted (cross-manufacturer safe codes).
 * 2. If all reefer containers share the SAME manufacturer, manufacturer-specific
 *    repair code prefix rules apply:
 *    - STARCOOL (SF models): codes starting with '0' or '7'
 *    - STARCOOL (other models): codes starting with '0' or '1'
 *    - DAIKIN: codes starting with '0' or '2'
 *    - CARRIER (NATURALINE): codes starting with '0' or '4'
 *    - CARRIER (other models): codes starting with '0' or '5'
 *    - THERMO KING: codes starting with '0' or '6'
 */
@Injectable({ providedIn: 'root' })
export class ContainerManufacturerValidator {
  private readonly bulkWorkOrderSharedDataService = inject(BulkWorkOrderSharedDataService);

  validate(selectedRepairCodes: string[], repairs: Repair[]): Observable<BulkWorkOrderManufacturerError> {
    return combineLatest([
      this.bulkWorkOrderSharedDataService.ValidatedEquipments,
      this.bulkWorkOrderSharedDataService.BulkWorkOrderHeader,
      this.bulkWorkOrderSharedDataService.Mode,
    ]).pipe(
      map(([validEquipments, bulkWorkOrderHeader, mode]) => {
        const emptyResult: BulkWorkOrderManufacturerError = {
          errorEquipments: new Map<string, Equipment[]>(),
          hasMixedManufacturers: false,
        };

        if (mode?.code !== REEFER_MODE_CODE) {
          return emptyResult;
        }

        const enteredEquipmentNumbers = new Set(bulkWorkOrderHeader.equipments.map((x) => x.equipmentNumber));
        const equipments = validEquipments.filter((x) => enteredEquipmentNumbers.has(x.equipmentNumber ?? ''));
        const reeferEquipments = equipments.filter((x) => x.equipmentTypeCode === 'REEF');

        if (reeferEquipments.length === 0) {
          return emptyResult;
        }

        const manufacturers = [
          ...new Set(reeferEquipments.map((x) => x.reeferUnitManufacturerName?.toUpperCase() ?? '').filter((x) => x !== '')),
        ];
        const hasMixedManufacturers = manufacturers.length > 1;
        const errorEquipments = new Map<string, Equipment[]>();

        selectedRepairCodes.forEach((repairCode) => {
          if (!repairCode) return;

          if (hasMixedManufacturers) {
            if (!repairCode.startsWith('0')) {
              errorEquipments.set(repairCode, reeferEquipments);
            }
          } else {
            const allowedPrefixes = this.getAllowedPrefixesForManufacturer(manufacturers[0], reeferEquipments[0]);
            if (allowedPrefixes !== null && !allowedPrefixes.some((prefix) => repairCode.startsWith(prefix))) {
              errorEquipments.set(repairCode, reeferEquipments);
            }
          }
        });

        return { errorEquipments, hasMixedManufacturers };
      }),
    );
  }

  /**
   * Returns the allowed repair code prefixes for a given manufacturer and equipment.
   * Returns null if no manufacturer-specific restriction applies.
   */
  private getAllowedPrefixesForManufacturer(manufacturer: string, equipment: Equipment): string[] | null {
    switch (manufacturer) {
      case 'STARCOOL':
        return equipment.reeferUnitModel?.toUpperCase().startsWith('SF') ? ['0', '7'] : ['0', '1'];
      case 'DAIKIN':
        return ['0', '2'];
      case 'CARRIER':
        return equipment.reeferUnitModel?.toUpperCase() === 'NATURALINE' ? ['0', '4'] : ['0', '5'];
      case 'THERMO KING':
        return ['0', '6'];
      default:
        return null;
    }
  }
}
